import type { MentionDraft } from '../mention-textarea/mention-textarea.component'

// TODO: use https://dexie.org/

export interface CorpMemberState {
  time_with_us: string
  service_state: string
  _location: string
  _fullname?: string
  id: number
  travel_from_city?: string // city they traveled from
  travel_from_state?: string // state they traveled from
  accommodation_location?: string
  region_street?: string
  city_or_town?: string
  email: string
  lga?: string
  lga_id: number
  stream?: string
  created_at: string
  batch?: string
  state_code: string
  updated_at: string
  media_id?: number // why?
  ppa_id?: number
  password?: string // would be better if not here.
  middle_name?: string // usually an empty string
  first_name?: string
  last_name?: string
  want_spa_or_not?: boolean
  looking_for_accommodation_or_not?: string
  public_profile?: string
  nickname?: string
  bio?: string
  state_short_name?: string
  PPA?: PpaModel
  Media?: MediaType
  slug_id?: string // for the profile link
  ProfileLinks?: ProfileLink[]

  signed_up_as_prospective_corp_member?: boolean
  email_verified_at?: string | null
  email_verification_snoozed_until?: string | null

  service_year_end_date?: string | null
  onboarding_profile_completed_at?: string | null
  onboarding_wishlist_completed_at?: string | null
  onboarding_push_completed_at?: string | null
  onboarding_prompt_shown_at?: string | null
  onboarding_snoozed_until?: string | null
}

export type PublicCorpMember = Pick<
  CorpMemberState,
  | 'state_code'
  | 'nickname'
  | 'first_name'
  | 'id'
  | 'slug_id'
  | 'public_profile'
  | 'created_at'
  | 'Media'
>

export type FeedDataType = SaleType | any // TODO: AccommodationType

export type ChatMessage = {
  age: string
  created_at: string
  id: number | string
  message: string
  /** CorpMembers.slug_id, not a numeric id. */
  message_from: string
  message_sent: boolean
  /** CorpMembers.slug_id, not a numeric id. */
  message_to: string
  read_by_to: boolean
  room: string
  updated_at: string
  _time_read: string
  FromCorpMember: PublicCorpMember
  ToCorpMember: PublicCorpMember
  /** True for the sender's own messages the server hasn't saved yet - the chat outbox's bubbles. */
  pending?: boolean
  /** Only on a pending message: where its send is at (see OutboxEntry). */
  send_status?: OutboxStatus
  /**
   * The sender's id for this message, made when it's first sent and kept across retries - the
   * backend saves it and won't save a resend twice. The outbox entry is matched by it too.
   */
  client_msg_id?: string | null
  /** True for admin-sent messages (e.g. national chat announcements). */
  is_admin_post?: boolean
  /** True for announcement messages (e.g. in national chat). */
  is_announcement?: boolean
  /** id of the ChatMessage this message is replying to, if any. */
  reply_to_chat_id?: number | null
  /**
   * The listing this message is about - set on the first message of a chat started from one ("Chat
   * seller" / "Chat"). The backend keeps it only if one of the two people posted it.
   */
  listing_type?: ChatListingRef['listing_type'] | null
  /** Sales.id / Accommodations.id - the backend saves the id of the listing whose slug was sent. */
  listing_id?: number | null
  /** The listing this message is about, for its "About" card - the backend's preview of listing_type / listing_id. */
  Listing?: ChatListingPreview | null
  /** When the recipient read it (direct chats only; the national chat has no per-message reads). Null until then. */
  time_read?: string | null
  /** Denormalized preview of the replied-to message, sent inline so the UI never has to look it up. */
  ReplyTo?: {
    id: number
    message: string
    /** CorpMembers.slug_id, not a numeric id. */
    message_from: string
    FromCorpMember: PublicCorpMember
  } | null
}

/**
 * What a chat message about a listing shows of it: the backend's `Listing`
 * (corpers.online's services/chatListingPreviewService.js). Null once the listing is gone for good.
 */
export type ChatListingPreview = {
  type: 'sale' | 'accommodation'
  slug_id: string
  /** A sale's item name, or a house's accommodation_type (as posted - see accommodationTypeLabel). */
  title: string
  /** A sale's price or a house's rent. */
  price: number | null
  /** The cover photo, if it has one. */
  photo_url: string | null
  /** Deleted by its poster (it's kept for 14 days, then purged). */
  deleted: boolean
  /** Sales only. */
  sold_at?: string | null
  /** Houses only. */
  rent_interval?: string
}

/**
 * - `sending`: waiting on its ack
 * - `waiting`: the socket is down - sent by itself once it's back
 * - `failed`: "Not sent" - see `OutboxEntry.error`
 */
export type OutboxStatus = 'sending' | 'waiting' | 'failed'

/** Why a message wasn't sent: a backend FAILED ack's code, or TIMEOUT when no ack came. */
export type ChatSendErrorCode =
  | 'INVALID_MESSAGE'
  | 'RECIPIENT_NOT_FOUND'
  | 'NOT_IDENTIFIED'
  | 'NOT_A_PARTICIPANT'
  | 'SERVER_ERROR'
  | 'TIMEOUT'

/** One of the sender's messages the server hasn't saved yet. */
export type OutboxEntry = {
  /** The bubble shown for it, in `message.room`. */
  message: ChatMessage
  /** Exactly what's sent - a retry resends it as is, under the same client_msg_id. */
  outgoing: OutgoingChatMessage
  /** The compose box's text and @mentions, for "Edit" (not the wire format, @[[id:Name]]). */
  draft: MentionDraft
  status: OutboxStatus
  /** Set when `status` is `failed`. */
  error?: ChatSendErrorCode
  /** Resent by itself when the socket (re)connects - false once the backend has turned it down. */
  autoRetry: boolean
}

/** The chat outbox, by client_msg_id. */
export type ChatOutbox = Record<string, OutboxEntry>

/** A CHAT_MESSAGE as the frontend sends it over the socket. */
export type OutgoingChatMessage = {
  client_msg_id: string
  message: string
  room?: string
  /** CorpMembers.slug_id - the backend uses the socket's verified identity instead. */
  message_from: string
  message_to?: string
  reply_to_chat_id?: number
} & Partial<ChatListingRef>

/** A sale or accommodation a chat message is about, by its public slug_id (never the numeric id). */
export type ChatListingRef = {
  listing_type: 'sale' | 'accommodation'
  listing_slug: string
}

export type AppMessageValue = {
  texts: Array<ChatMessage>
  unread_messages: number
  initiator_name?: string
  initiator_state_code?: string
  recipient_state_code?: string
  recipient_name?: string // full name | nickname | state_code
  /** CorpMembers.slug_id, not a numeric id. */
  recipient_id?: string
  /** CorpMembers.slug_id, not a numeric id. */
  initiator_id?: string
  room?: string // won't be used when starting a new chat

  chat_display_name?: string
  chat_display_state_code?: string
}

/**
 * Should the map key be the room??? No cause what about new messages? And how would we save drafts?
 * Map keys should be the other participant in the chat?
 * No, room key should be generated from the client device?
 */
export type AppMessages = Map<string | number | undefined, AppMessageValue>

export type AppFeedState = {
  sales: Map<number | string, SaleType>
  accommodations: Map<number | string, AccommodationType>
  /** When the feed was last populated from GET /all-items, or null if it's only ever seen socket broadcasts. */
  lastFetchedAt: number | null
  /**
   * Ids of the sales the logged-in corp member saved (GET /sale/bookmarks), or null until loaded.
   * Kept here, not on each sale: /all-items is cached for every member alike, so it can't say.
   */
  savedSaleIds: Set<number> | null

  /** Ids of the houses the logged-in corp member saved (GET /accommodation/bookmarks), or null until loaded. */
  savedAccommodationIds: Set<number> | null
}

export type PostHistoryState = Map<string, SaleType | AccommodationType>

/** The /ppa page's current page of PPAs (GET /ppas), and how many match across all pages. */
export type PpasState = {
  list: PpaModel[]
  total: number
}

/** The per-PPA numbers a new review changes (POST /ppa/review sends them back as `ppa_stats`). */
export type PpaReviewStats = Pick<PpaModel, 'review_count' | 'avg_rating' | 'latest_review' | 'rating_breakdown'>

export interface AppState {
  corper: CorpMemberState
  feed: AppFeedState

  /**
   * A Map of strings (state codes), and an object of properties, and the text messages.
   */
  messages: AppMessages

  posted_items: PostHistoryState

  ppas: PpasState

  /** The sender's messages the server hasn't saved yet - see ChatOutboxEffects. */
  chat_outbox: ChatOutbox
}

export interface PpaModel {
  id: number
  slug_id?: string
  name: string
  type_of_ppa: string
  created_at: string
  updated_at: string
  Locations?: Array<LocationModel>
  Reviews?: Array<ReviewModel>
  Media?: Array<MediaType>
  PPALikes?: Array<PPALikeType>
  PPABookmarks?: Array<PPABookmarkType>

  // Stats from GET /ppas, /search and /all-items (PPA.getStatsAttributes() on the backend).
  // Other endpoints may not send them.
  review_count?: number
  avg_rating?: string | number | null // MySQL decimal, arrives as a string
  corper_count?: number
  houses_nearby_count?: number
  latest_review?: string | null // newest non-empty review comment - lists no longer include every review

  // With Reviews (GET /ppas/:id, /search, /all-items), they're just the newest page - GET /ppas/:id/reviews?before= has the rest.
  reviews_next_cursor?: number | null // null when Reviews is all of them
  rating_breakdown?: RatingBreakdown
}

/** How many ratings of each number of stars (each corp member once, like avg_rating). */
export type RatingBreakdown = Record<1 | 2 | 3 | 4 | 5, number>

/** GET /ppas/:id/reviews - a page of reviews, newest first. */
export interface ReviewPage {
  reviews: ReviewModel[]
  next_cursor: number | null
}

export interface StateDetailsModel {
  created_at: string
  id: number
  name: string
  short_name: string
  updated_at: string
}

export interface LocationModel {
  type: string
  id: number
  ppa_id: number
  accommodation_id?: number
  directions?: number
  address: string
  lat?: number | null
  lng?: number | null
  state_lga_id: number
  state_id: number
  corp_member_id?: number
  created_at: string
  updated_at: string
  CorpMemberId?: number

  /** null on older locations saved with only an LGA - corpers.online fills it in now, but show the LGA alone if it's missing. */
  State?: StateDetailsModel | null
  StateLGA?: StateDetailsModel | null
}

export interface ReviewModel {
  id: number
  ppa_id: number
  // accommodation_id?: number, will implement
  comment: string
  star_rating: number | null
  is_anonymous?: boolean
  /** Who wrote it, by public profile fields (slug_id etc.) - null on anonymous reviews. The API doesn't send corp_member_id. */
  CorpMember?: PublicCorpMember | null
  created_at: string
  updated_at: string
  /** "3 days ago" (a virtual field on the backend) */
  _age?: string
}

export interface ProfileLink {
  id: number
  label: string
  url: string
  corp_member_id: number
  created_at: string
  updated_at: string
}

export interface MediaType {
  id: number
  url: string
  sale_id: number | number
  updated_at: string
  created_at: string

  accommodation_id: null | number
  alt_text: null
  chat_id: null | number
  corp_member_id: null | number

  location_id: null | number
  ppa_id: null | number
}

// @todo: should this be updated with more fields/properties?
interface CorpMemberType {
  first_name: string
  nickname?: string
  service_state: string
  state_code: string
  _location: string
  id: number
  slug_id?: string
  /** DATEONLY; the card says "Leaving in <month>" when it's near. Often unset. */
  service_year_end_date?: string | null

  created_at: string
  updated_at?: string
}

interface SaleBookmarkType {
  corp_member_id: number
  created_at: string
  id: number
  sale_id: number
  updated_at: string
}

interface PPALikeType {
  corp_member_id: number
  created_at: string
  id: number
  ppa_id: number
  updated_at: string
}

interface PPABookmarkType {
  corp_member_id: number
  created_at: string
  id: number
  ppa_id: number
  updated_at: string
}

export interface SaleType {
  _price: string // parsable to number
  _age: string
  last_updated_age: string
  _type: 'sale'
  id: number
  slug_id?: string
  corp_member_id: number
  text: string
  item_name: string
  minimum_price?: number
  price: number
  updated_at: string
  created_at: string

  Media?: Array<MediaType>

  CorpMember?: CorpMemberType

  SaleBookmarks?: SaleBookmarkType[]

  /** When the seller marked it sold, else null. Feeds only get sales sold in the last 24 hours. */
  sold_at?: string | null
  /** One of SALE_CONDITIONS (utils/sale.util.ts); null on older posts. */
  condition?: string | null
  /** One of SALE_CATEGORIES (utils/sale.util.ts); null on older posts. */
  category?: string | null

  /** Set when post was created by admin */
  is_admin_post?: boolean
}

export interface AccommodationType {
  _age: string
  _type: 'accommodation'

  accommodation_type: string
  age: string
  available_rooms: string[]

  corp_member_id: number
  created_at: string
  id: number
  slug_id?: string
  ideal_roommate: string
  location_id: null
  occupant_description: string
  rent: number
  rent_expire_date: null
  rent_interval: string
  roommate_rent: number | null
  status: string | null
  _status: string | null
  updated_at: string
  _rent: string
  _roommate_rent: null

  Media?: Array<MediaType>

  CorpMember?: CorpMemberType


  AccommodationBookmarks?: SaleBookmarkType[] // @todo: update type

  /** Set when post was created by admin */
  is_admin_post?: boolean

  Location?: any

  contactPhoneNumbers?: Array<{
    number: string
    label: string
  }>
}

export const initialCorpMemberState: CorpMemberState = {
  time_with_us: '',
  service_state: '',
  _location: '',
  id: 0, // 0 since it's initial state
  travel_from_city: undefined,
  travel_from_state: undefined,
  accommodation_location: undefined,
  region_street: undefined,
  city_or_town: undefined,
  email: '',
  lga: undefined,
  lga_id: 0,
  stream: undefined,
  created_at: '',
  batch: undefined,
  state_code: '',
  updated_at: '',
  media_id: undefined, // why?, maybe for pfp?
  ppa_id: undefined,
  password: undefined, // would be better if not here.
  middle_name: '', // usually an empty string
  first_name: '',
  last_name: '',
  want_spa_or_not: undefined,
  looking_for_accommodation_or_not: undefined,
  public_profile: undefined,
  nickname: undefined,
  bio: undefined,
  state_short_name: undefined,
  signed_up_as_prospective_corp_member: undefined,
  email_verified_at: undefined,


  Media: undefined,
  PPA: undefined,
}

const testData = [{}, {}, {}, {}].fill(
  {
    _price: '423,244',
    _age: 'a few seconds ago',
    last_updated_age: 'a few seconds ago',
    _type: 'sale',
    id: 5,
    corp_member_id: 2,
    text: 'Blueyuyy',
    item_name: 'Hello',
    price: 423244,
    Media: [
      {
        id: 4,
        url: 'https://chuks.name.ng/chuks.name.ng/corpers_ng_data/f5287e2ed12d19cb25deeadc9b014e9abe74c987.png',
        sale_id: 5,
        updated_at: '2024-05-03T07:14:32.103Z',
        created_at: '2024-05-03T07:14:32.103Z',
      },
      {
        id: 5,
        url: 'https://chuks.name.ng/chuks.name.ng/corpers_ng_data/89e1e5d3ae0fe38a08e1203d306652a479131730.png',
        sale_id: 5,
        updated_at: '2024-05-03T07:14:32.104Z',
        created_at: '2024-05-03T07:14:32.104Z',
      },
    ],
    updated_at: '2024-05-03T07:14:31.978Z',
    created_at: '2024-05-03T07:14:31.978Z',
  },
  0,
  4
)

export const initialState: AppState = {
  corper: initialCorpMemberState,
  feed: {
    sales: new Map(),
    accommodations: new Map(),
    lastFetchedAt: null,
    savedSaleIds: null,
    savedAccommodationIds: null,
  },
  messages: new Map(),
  posted_items: new Map(),
  ppas: { list: [], total: 0 },
  chat_outbox: {},
}

export type FeedViewType = 'sale' | 'accommodation'
